/**
 * PmsReservations — `/pms/reservations`, el listado de la vista previa V2.
 *
 * Reservas es la fuente operativa de una estancia: quién reservó, qué,
 * cuándo, por dónde entró, cuánto vale, qué se ha cobrado, si el huésped está
 * preparado, en qué unidad física está y si algo necesita atención.
 *
 * Tres conceptos que NO se mezclan (PRODUCT_V2_SPEC §6.1):
 *   reserva ≠ huésped ≠ unidad física.
 * Una reserva se vende sobre un TIPO de alojamiento y puede recibir después
 * su unidad concreta.
 *
 * Todo sale del dataset de la demo PMS y de Ajustes V2: vocabulario, tipos,
 * horarios, servicios, impuestos y moneda. Sin backend, sin pasarela de pago y
 * sin channel manager.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { useMediaQuery, DESKTOP_QUERY } from "@/hooks/useMediaQuery";
import { Plus, Info } from "@phosphor-icons/react";

import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { useSettingsV2, unitLabelFor } from "@/lib/settingsV2Store";
import { usePmsReservations } from "@/lib/pmsRuntimeStore";
import { useTodayV2 } from "@/lib/todayV2Store";
import { useReservationsV2 } from "@/lib/reservationsV2Store";
import { useGuestsV2 } from "@/lib/guestsV2Store";
import { enrichRequests } from "@/lib/todayV2Selectors";
import {
  buildReservationView, filterReservationViews, quickFilterCounts, currencyFor,
} from "@/lib/reservationsV2Selectors";
import {
  SUMMARY_KEYS, SUMMARY_LABEL, RESERVATIONS_DEMO_NOTE,
} from "@/data/reservationsV2Demo";
import { SURFACE, PreviewToast } from "@/pages/admin/pms/v2/V2Primitives";
import ReservationsToolbar from "@/pages/admin/pms/reservations/ReservationsToolbar";
import { ReservationsTable, ReservationsCards } from "@/pages/admin/pms/reservations/ReservationsList";
import NewReservationWizard from "@/pages/admin/pms/reservations/NewReservationWizard";

const EMPTY_FILTERS = {
  booking_status: "all",
  stay_status: "all",
  payment_status: "all",
  channel: "all",
  unit_type: "all",
  unit: "all",
  from: "",
  to: "",
};

export default function PmsReservations() {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const settings = useSettingsV2();
  const reservations = usePmsReservations();
  const todayState = useTodayV2();
  const reservationsState = useReservationsV2();
  const guestState = useGuestsV2();

  const unitLabel = useMemo(() => unitLabelFor(settings.property), [settings.property]);
  // Las monedas configuradas de la propiedad son metadato. Los importes se
  // enseñan en la moneda del dato y no se convierte ninguna cifra.
  const currency = useMemo(() => currencyFor(settings), [settings]);
  const requests = useMemo(
    () => enrichRequests({ state: todayState, services: settings.services, unitLabel }),
    [todayState, settings.services, unitLabel],
  );

  const views = useMemo(() => reservations
    .map((reservation) => buildReservationView({
      reservation, settings, requests, storeState: reservationsState,
      guestState, today: DEMO_TODAY_ISO,
    }))
    // Lo vigente primero y en orden cronológico; lo pasado, detrás.
    .sort((a, b) => {
      const pastA = a.reservation.check_out < DEMO_TODAY_ISO ? 1 : 0;
      const pastB = b.reservation.check_out < DEMO_TODAY_ISO ? 1 : 0;
      if (pastA !== pastB) return pastA - pastB;
      return a.reservation.check_in.localeCompare(b.reservation.check_in);
    }),
  [reservations, settings, requests, reservationsState, guestState]);

  const [query, setQuery] = useState("");
  const [quick, setQuick] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const counts = useMemo(() => quickFilterCounts(views, DEMO_TODAY_ISO), [views]);
  const filtered = useMemo(
    () => filterReservationViews({ views, query, quick, filters, today: DEMO_TODAY_ISO }),
    [views, query, quick, filters],
  );

  return (
    <PmsShell>
      <div className="mb-4" data-testid="reservations-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Reservas</div>
            <h1 className="font-serif-dv text-2xl sm:text-3xl text-stone-900 dark:text-stone-100 truncate">
              {settings.property.name}
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            data-testid="reservations-new"
            className="shrink-0 min-h-[44px] px-4 rounded-full text-xs inline-flex items-center gap-1.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
          >
            <Plus size={13} weight="bold" />
            Nueva reserva
          </button>
        </div>

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-stone-600 dark:text-stone-300" data-testid="reservations-summary">
            {SUMMARY_KEYS.map((key) => (
              <span key={key}>
                <span className="tabular-nums text-stone-900 dark:text-stone-100">{counts[key]}</span>
                {" "}
                <span className="text-stone-500 dark:text-stone-400">{SUMMARY_LABEL[key].toLowerCase()}</span>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setNoteOpen((v) => !v)}
            aria-expanded={noteOpen}
            aria-label="Qué es esta vista previa"
            data-testid="reservations-demo-note-toggle"
            className="w-11 h-11 -my-2 flex items-center justify-center text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
          >
            <Info size={14} />
          </button>
        </div>

        {noteOpen && (
          <p
            className="mt-2 text-[11px] leading-relaxed text-stone-500 dark:text-stone-400 max-w-2xl"
            data-testid="reservations-demo-note"
          >
            {RESERVATIONS_DEMO_NOTE} El vocabulario, los tipos de alojamiento, los horarios, los
            servicios y los impuestos se leen de Ajustes.
          </p>
        )}
      </div>

      <ReservationsToolbar
        query={query}
        onQuery={setQuery}
        quick={quick}
        onQuick={setQuick}
        counts={counts}
        filters={filters}
        onFilters={setFilters}
        settings={settings}
        unitLabel={unitLabel}
        desktop={desktop}
      />

      <div className={`${SURFACE} px-3 py-2 mb-2 text-[11px] text-stone-500 dark:text-stone-400 flex items-center justify-between gap-2`}>
        <span data-testid="reservations-count">
          {filtered.length} de {views.length} reservas
        </span>
        <span
          className="text-stone-400"
          data-testid="reservations-currency-note"
          title={`La propiedad opera en ${currency.operational || "—"} e informa en ${currency.reporting || "—"}. Los importes de la vista previa están en ${currency.dataset} y no se convierten.`}
        >
          Importes en {currency.dataset}
          {!currency.reporting_available && currency.reporting
            ? ` · sin equivalente en ${currency.reporting}`
            : ""}
        </span>
      </div>

      {desktop
        ? <ReservationsTable views={filtered} />
        : <ReservationsCards views={filtered} />}

      {wizardOpen && (
        <NewReservationWizard
          settings={settings}
          unitLabel={unitLabel}
          onClose={() => setWizardOpen(false)}
          onCreated={() => showToast("Creada en la vista previa")}
        />
      )}

      <PreviewToast text={toast} testId="reservations-toast" />
    </PmsShell>
  );
}
